export interface DescriptionState {
    id: string;
    idLanguage: number;
    languageCode: string;
    language: string;
    descriptionContent: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface ServiceState {
    id: string;
    name: string;
    free: boolean;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export enum AttributeDefinitionFieldType {
    BOOLEAN = 'Boolean',
    STRING = 'String',
    NUMBER = 'Numeric',
    DATE = 'Date',
}

export enum AttributeDefinitionSizeKeyType {
    SMALL = 'SMALL',
    MEDIUM = 'MEDIUM',
    LARGE = 'LARGE',
}

export interface AttributeDefinitionState {
    id: string;
    idAttributeDefinition: number;
    required: boolean;
    fieldType: AttributeDefinitionFieldType;
    name: string;
    unique: boolean;
    sizeKey: AttributeDefinitionSizeKeyType;
    size: number;
    idCategory: number;
    category: string;
}

export interface AttributeValueState {
    id: string;
    idAttributeDefinition: number;
    value: string | boolean | Date | number | null;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface ZinantisServiceState {
    id: string;
    serviceType: string;
    free: boolean;
    idServiceType: number;
    idServiceCategory: number;
    serviceCategory: string;
    attributeDefinitions?: AttributeDefinitionState[];
    attributeValues?: AttributeValueState[];
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface HostSegmentState {
    id: string;
    name: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface MealPlanTypeState {
    id: string;
    name: string;
    mappingCode: string;
    idType: number;
    typeName: string;
    typeMappingCodeTemplate: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface ImageState {
    id: string;
    caption: string;
    feature: boolean;
    published: boolean;
    is360: boolean;
    idType: number;
    type: string;
    url: string;
    file?: File | null;
    idRoom: number;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

interface BedTypeState {
    id: string;
    name: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface RoomState {
    id: string;
    name: string;
    adultsOnly: boolean;
    freeBaby1: boolean;
    freeBaby2: boolean;
    occupancy: string;
    minAdults: number;
    maxAdults: number;
    minChildren: number;
    maxChildren: number;
    standardOccupancy: number;
    size: number;
    quantity: number;
    images: ImageState[];
    services: ServiceState[];
    bedTypes: BedTypeState[];
    descriptions: DescriptionState[];
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface NoteState {
    id: string;
    message: string;
    fromBookingDate: boolean;
    fromDate: Date | null;
    toDate: Date | null;
    enabled: boolean;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface PropertyTaxState {
    id: string;
    name: string;
    description: string;
    fromDate: Date | null | string;
    toDate: Date | null | string;
    enabled: boolean;
    idRoom: number;
    room: string;
    idMealPlan: number;
    mealPlan: string;
    idRatePlan: number;
    ratePlan: string;
    idTaxType: number;
    taxType: string;
    taxTypeKey: string;
    idTaxCalculationType: number;
    taxCalculationType: string;
    taxCalculationTypeKey: string;
    idTaxCalculationRuleType: number;
    taxCalculationRuleType: string;
    taxCalculationRuleTypeKey: string;
}

export interface PropertySupplementState {
    id: string;
    name: string;
    description: string;
    fromDate: Date | null | string;
    toDate: Date | null | string;
    enabled: boolean;
    idRoom: number;
    room: string;
    idMealPlan: number;
    mealPlan: string;
    idRatePlan: number;
    ratePlan: string;
    idTaxCalculationType: number;
    taxCalculationType: string;
    taxCalculationTypeKey: string;
    idTaxCalculationRuleType: number;
    taxCalculationRuleType: string;
    taxCalculationRuleTypeKey: string;
}

export interface PropertyStopSaleState {
    id: string;
    fromDate: Date | null | string;
    toDate: Date | null | string;
    enabled: boolean;
    idRoom: number;
    room: string;
    idMealPlan: number;
    mealPlan: string;
    idRatePlan: number;
    ratePlan: string;
}

export interface ChildrenAgeSegmentState {
    id: string;
    age: number;
    name: string;
    key: string;
    isBaby: boolean;
    isFirstChild: boolean;
    isOtherChild: boolean;
    isSecondChild: boolean;
    __isAdded?: boolean;
    __isChanged?: boolean;
    Deleted?: boolean;
}

export interface PropertyState {
    id: string;
    name: string;
    primary: boolean; // indicates if this is the primary supplier hotel, which is the only one who can edit data from the hotel
    idHotel: number;
    idSupplier: number;
    hotelCategory: string;
    rating: number; // decimal
    address: string;
    email: string;
    phone: string;
    bookingPhone: string;
    bookingEmail: string;
    accountingPhone: string;
    accountingEmail: string;
    idCountry: number;
    idRegion: number;
    idProvince: number;
    idCity: number;
    postalCode: string;
    idAirportCode: string;
    airportCode: string;
    idTimeZone: number;
    idHotelType: number;
    idHotelCategory: number;
    idHotelChain: number;
    idCurrency: number;
    checkInHour: string;
    checkOutHour: string;
    roomCount: number;
    adultsOnly: boolean;
    adultAge: number;
    childrenAgeSegments: ChildrenAgeSegmentState[];
    descriptions: DescriptionState[];
    services: ServiceState[];
    zitoneoServices: ZinantisServiceState[];
    attributeDefinitions: AttributeDefinitionState[];
    attributeValues: AttributeValueState[];
    hostSegments: HostSegmentState[];
    mealPlanTypes: MealPlanTypeState[];
    images: ImageState[];
    rooms: RoomState[];
    notes: NoteState[];
    taxes: PropertyTaxState[];
    supplements: PropertySupplementState[];
    stopSales: PropertyStopSaleState[];
}

export type ProgressArrayItem = {
    id: string;
    step: string;
    tab: string;
    __isAdded?: boolean;
    __isChanged?: boolean;
};

export type ProgressInfo = {
    percent: number;
    lastTabCompleted: number;
};

export type ProgressByStepState = {
    [key in PROGRESS_STEP]: ProgressInfo;
};

export interface ProgressState {
    totalProgress: number;
    progressByStep: ProgressByStepState;
    activeStep: PROGRESS_STEP | '';
    activeTab: number;
    activeTabKey:
        | PROGRESS_MAIN_INFORMATION_TAB
        | PROGRESS_PUBLIC_PORTRAIT_TAB
        | PROGRESS_ROOM_CONFIGURATION_TAB
        | '';
    progressArray: ProgressArrayItem[];
}

export enum PROGRESS_STEP {
    MAIN_INFORMATION = 'main_info',
    PUBLIC_PORTRAIT = 'description',
    ROOM_CONFIGURATION = 'room_configuration',
}

export enum URL_STEP {
    MAIN_INFORMATION = 'management',
    PUBLIC_PORTRAIT = 'public-portrait',
    ROOM_CONFIGURATION = 'inventory',
}
export enum PROGRESS_MAIN_INFORMATION_TAB {
    COMMERCIAL = 'Commercial',
    CONTACT = 'Contact',
    LOCATION = 'Location',
    AREA = 'Area',
    PROPERTY = 'Property',
}

export enum PROGRESS_PUBLIC_PORTRAIT_TAB {
    KID_POLICIES = 'Kid policies',
    SEGMENTS = 'Segments',
    SERVICES = 'services',
    MEAL_PLANS = 'Meal Plans',
    DESCRIPTIONS = 'Descriptions',
    SPECIAL_NOTES = 'Special Notes',
    IMAGES = 'Images',
}

export enum PROGRESS_ROOM_CONFIGURATION_TAB {
    ROOM_INFO = 'Main room information',
    OCCUPANCY = 'Occupancy',
    SERVICES = 'Attributes',
    BEDS = 'Beds',
    DESCRIPTIONS = 'Descriptions',
    IMAGES = 'Images',
}

export interface ComplementaryCountryState {
    id: string;
    name: string;
}
export interface ComplementaryRegionState {
    id: string;
    name: string;
}
export interface ComplementaryProvinceState {
    id: string;
    name: string;
}
export interface ComplementaryCityState {
    id: string;
    name: string;
}
export interface ComplementaryTimeZoneState {
    id: string;
    name: string;
}

export interface ComplementaryHotelTypeState {
    id: string;
    name: string;
}

export interface ComplementaryHotelCategoryState {
    id: string;
    name: string;
}

export interface ComplementaryHotelChainState {
    id: string;
    name: string;
}

export interface ComplementaryCurrencyState {
    id: string;
    name: string;
}

export interface ComplementaryHostSegmentState {
    id: string;
    name: string;
}

export interface ComplementaryMealPlanTypeState {
    id: string;
    name: string;
    mappingCodeTemplate: string;
}

export interface ComplementaryServiceCategoryState {
    id: string;
    name: string;
}

export interface ComplementaryServiceTypeState {
    id: string;
    name: string;
    idServiceCategory: number;
    free: boolean;
}

export interface ComplementaryBedTypeState {
    id: string;
    name: string;
}

export interface ComplementaryImageTypeState {
    id: string;
    name: string;
    nameKey: string;
}

export interface ComplementaryLanguageState {
    id: string;
    name: string;
    cultureId: string;
}

export interface ComplementaryChildrenAgeSegmentState {
    id: string;
    name: string;
    key: string;
    isBaby: boolean;
    isFirstChild: boolean;
    isSecondChild: boolean;
    isOtherChild: boolean;
}

export interface ComplementaryDataState {
    countries: ComplementaryCountryState[];
    regions: ComplementaryRegionState[];
    provinces: ComplementaryProvinceState[];
    cities: ComplementaryCityState[];
    timeZones: ComplementaryTimeZoneState[];
    hotelTypes: ComplementaryHotelTypeState[];
    hotelCategories: ComplementaryHotelCategoryState[];
    hotelChains: ComplementaryHotelChainState[];
    currencies: ComplementaryCurrencyState[];
    hostSegments: ComplementaryHostSegmentState[];
    mealPlanTypes: ComplementaryMealPlanTypeState[];
    serviceCategories: ComplementaryServiceCategoryState[];
    serviceTypes: ComplementaryServiceTypeState[];
    roomServiceCategories: ComplementaryServiceCategoryState[];
    roomServiceTypes: ComplementaryServiceTypeState[];
    beds: ComplementaryBedTypeState[];
    hotelImagesTypes: ComplementaryImageTypeState[];
    languages: ComplementaryLanguageState[];
    childrenAgeSegments: ComplementaryChildrenAgeSegmentState[];
}

export interface DescriptionDetailState extends DescriptionState {
    isOpenPanel: boolean;
}

export interface NoteDetailState extends NoteState {
    isOpenPanel: boolean;
}

export interface ImagesDetailState {
    images: ImageState[];
    isOpenPanel: boolean;
    isDropdownVisible: boolean;
    category?: ComplementaryImageTypeState | null;
    idRoom: number;
    isLoading: boolean;
}

export interface ValidationErrorsState {
    name: string;
    phone: string;
    email: string;
    address: string;
    idCountry: string;
    idCity: string;
    postalCode: string;
    idTimezone: string;
    idHotelType: string;
    idHotelCategory: string;
    roomAmount: string;
    idCurrency: string;
    childrenAgeSegments: string;
}

export enum PresentationTabNames {
    HOTEL = 0,
    ZITONEO = 1,
}

export enum ZinantisHotelServiceFilterOption {
    ALL = 'all',
    SELECTED = 'selected',
    INCOMPLETE = 'incomplete',
    MISSING_REQUIRED = 'missing_required',
}
